// 360° skies from Meta Muse Image (~1¢, ~30 s), as an alternative to
// Blockade Skybox AI.
//
// Muse isn't a panorama model, but asked for a 2:1 equirectangular view it
// paints one (2240×1120). Two fixes make it work as a sky sphere:
//   - the left and right edges don't meet, so the last strip is cross-faded
//     into the first and the strip dropped (the picture then wraps);
//   - toward the top and bottom, rows shrink to single points on the sphere,
//     so detail there (clouds, ripples) would pinch into a starburst; rows are
//     blurred sideways more the nearer the pole, then faded to one colour.

import { generateMuseRaw } from './openRouterApi';
import { uploadBytesToStorage } from '../utils/uploadToStorage';

export interface SkyStyle { id: string; label: string; words: string }

export const SKY_STYLES: SkyStyle[] = [
  { id: 'realistic', label: 'Realistic', words: 'photorealistic, natural light, rich detail, high dynamic range' },
  { id: 'fantasy', label: 'Fantasy', words: 'epic fantasy concept art, luminous, magical atmosphere, painterly detail' },
  { id: 'anime', label: 'Anime', words: 'anime background art, Studio Ghibli inspired, soft cel shading, vivid colours' },
  { id: 'scifi', label: 'Sci-fi', words: 'cinematic science fiction matte painting, futuristic, glowing details' },
  { id: 'painterly', label: 'Painterly', words: 'impressionist oil painting, visible brush strokes, warm palette' },
  { id: 'dreamy', label: 'Dreamy', words: 'dreamy pastel colours, soft haze, ethereal light, surreal and calm' },
];

export function skyPrompt(idea: string, style: SkyStyle = SKY_STYLES[0]): string {
  return [
    'Equirectangular 360-degree panorama, 2:1, seamless left-right wrap, full spherical environment for a VR skybox:',
    `${idea.trim().replace(/\.$/, '')}.`,
    `${style.words}.`,
    'The horizon sits exactly at the vertical middle; open sky fills the top part and the ground or water the bottom part.',
    'Everything is far away: no close-up objects, no people, no text, no frame, no border.',
  ].join(' ');
}

const smooth = (t: number) => t * t * (3 - 2 * t);

/**
 * Make a panorama wrap and calm its poles, in place on the pixels. Returns
 * the new width (the cross-faded strip is dropped). Pure; exported for tests.
 */
export function fixPanorama(px: Uint8ClampedArray, w: number, h: number, seamFrac = 0.06, topFrac = 0.06, bottomFrac = 0.05): { data: Uint8ClampedArray; width: number } {
  const B = Math.max(2, Math.round(w * seamFrac));
  const W = w - B;
  const out = new Uint8ClampedArray(W * h * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < W; x++) {
      const o = (y * W + x) * 4;
      const a = (y * w + x) * 4;
      if (x < B) {
        // x=0 shows the dropped strip's start (what followed column W-1), x=B the original.
        const t = smooth(x / B), b = (y * w + W + x) * 4;
        for (let c = 0; c < 4; c++) out[o + c] = px[b + c] * (1 - t) + px[a + c] * t;
      } else {
        for (let c = 0; c < 4; c++) out[o + c] = px[a + c];
      }
    }
  }
  // Near the poles one row of pixels covers less and less of the sphere, so
  // the painted detail pinches into a starburst. Blur each row sideways
  // (wrapping) by a radius that grows like 1/cos(latitude), then fade the
  // last rows to their average colour.
  const row = new Float32Array((W + 1) * 3);
  for (let y = 0; y < h; y++) {
    const lat = Math.abs(0.5 - (y + 0.5) / h) * Math.PI; // 0 at the horizon → π/2 at a pole
    const r = Math.min(Math.floor(W / 2) - 1, Math.round((1 / Math.max(Math.cos(lat), 1e-3) - Math.SQRT2) * (W / 64)));
    if (r < 1) continue;
    for (let x = 0; x < W; x++) for (let c = 0; c < 3; c++) row[(x + 1) * 3 + c] = row[x * 3 + c] + out[(y * W + x) * 4 + c];
    const total = [row[W * 3], row[W * 3 + 1], row[W * 3 + 2]];
    const sum = (i: number, c: number) => (i < 0 ? row[(i + W) * 3 + c] - total[c] : i > W ? total[c] + row[(i - W) * 3 + c] : row[i * 3 + c]);
    for (let x = 0; x < W; x++) {
      const o = (y * W + x) * 4;
      for (let c = 0; c < 3; c++) out[o + c] = (sum(x + r + 1, c) - sum(x - r, c)) / (2 * r + 1);
    }
  }
  const fade = (rows: number, top: boolean) => {
    if (rows < 2) return;
    const avg = [0, 0, 0];
    const band = Math.max(1, Math.round(rows * 0.25));
    for (let r = 0; r < band; r++) {
      const y = top ? r : h - 1 - r;
      for (let x = 0; x < W; x++) for (let c = 0; c < 3; c++) avg[c] += out[(y * W + x) * 4 + c];
    }
    for (let c = 0; c < 3; c++) avg[c] /= band * W;
    for (let r = 0; r < rows; r++) {
      const y = top ? r : h - 1 - r;
      const k = smooth(1 - r / rows); // 1 at the pole → 0 at the band's edge
      for (let x = 0; x < W; x++) {
        const o = (y * W + x) * 4;
        for (let c = 0; c < 3; c++) out[o + c] = out[o + c] * (1 - k) + avg[c] * k;
      }
    }
  };
  fade(Math.round(h * topFrac), true);
  fade(Math.round(h * bottomFrac), false);
  return { data: out, width: W };
}

async function loadImg(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
  await img.decode();
  return img;
}

/** Muse picture → wrapped, pole-calmed 2:1 JPEG blob. */
export async function finishPanorama(src: string): Promise<Blob> {
  const img = await loadImg(src);
  const w = img.naturalWidth, h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0);
  const { data, width } = fixPanorama(g.getImageData(0, 0, w, h).data, w, h);
  const fixed = document.createElement('canvas');
  fixed.width = width; fixed.height = h;
  const fg = fixed.getContext('2d')!;
  const id = fg.createImageData(width, h);
  id.data.set(data);
  fg.putImageData(id, 0, 0);
  // Back to exactly 2:1 (the dropped strip is ~6%, a barely visible squeeze).
  const out = document.createElement('canvas');
  out.width = 2 * h; out.height = h;
  const og = out.getContext('2d')!;
  og.imageSmoothingQuality = 'high';
  og.drawImage(fixed, 0, 0, out.width, out.height);
  return new Promise((res, rej) => out.toBlob((b) => (b ? res(b) : rej(new Error('Could not save the sky.'))), 'image/jpeg', 0.92));
}

/** File a finished sky under "Your skies" so it can be reused in any world. */
async function saveSky(name: string, url: string, source: string) {
  const { saveToMyLibrary } = await import('./myLibrary');
  await saveToMyLibrary({ type: 'skybox', name, url, source });
}

/** Describe a place → a stored 360° sky. */
export async function generateMuseSky(
  idea: string, styleId = 'realistic', onStatus?: (s: string) => void, signal?: AbortSignal,
): Promise<{ url: string; filename: string }> {
  const style = SKY_STYLES.find((s) => s.id === styleId) || SKY_STYLES[0];
  onStatus?.('painting');
  const raw = await generateMuseRaw(skyPrompt(idea, style), '2048x1024', signal);
  onStatus?.('wrapping');
  const blob = await finishPanorama(raw);
  onStatus?.('saving');
  const url = await uploadBytesToStorage(blob, `skyboxes/muse-${Date.now()}.jpg`, 'image/jpeg');
  const filename = `AI: ${idea.trim().slice(0, 40)}`;
  void saveSky(filename, url, 'sky-muse');
  return { url, filename };
}

/**
 * High-quality tier: a true 360° model on our RunPod panorama worker
 * (supabase/functions/pano-sky → webdeeva/gathrxr-pano-worker). 4096×2048
 * Ultra HDR JPEG — straight lines stay straight (buildings, rooms, cities),
 * and the sun lights the scene. ~1–3 min; the worker saves it to R2 itself.
 */
export async function generateHqSky(
  idea: string, styleId = 'realistic', onStatus?: (s: string) => void,
): Promise<{ url: string; filename: string }> {
  const { supabase } = await import('../config/supabase');
  const call = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('pano-sky', { body });
    if (error) {
      const ctx: any = (error as any).context;
      const msg = ctx?.json ? (await ctx.json().catch(() => null))?.error : null;
      throw new Error(msg || error.message || 'Sky painter error');
    }
    return data;
  };
  const style = SKY_STYLES.find((s) => s.id === styleId) || SKY_STYLES[0];
  onStatus?.('starting');
  const { id } = await call({ action: 'submit', prompt: idea.trim(), style: style.words });
  const started = Date.now();
  while (Date.now() - started < 10 * 60_000) {
    await new Promise((r) => setTimeout(r, 4000));
    const st = await call({ action: 'status', id }).catch(() => ({ status: 'UNKNOWN' }));
    if (st.status === 'COMPLETED') {
      if (st.url) {
        const filename = `HQ: ${idea.trim().slice(0, 40)}`;
        void saveSky(filename, st.url, 'sky-hq');
        return { url: st.url, filename };
      }
      throw new Error(st.error || 'The sky painter returned nothing.');
    }
    if (['FAILED', 'CANCELLED', 'TIMED_OUT'].includes(st.status)) throw new Error(st.error || 'The sky painter failed.');
    onStatus?.(st.status === 'IN_PROGRESS' ? 'painting' : 'waiting for a GPU');
  }
  call({ action: 'cancel', id }).catch(() => {});
  throw new Error('The sky painter took too long.');
}
